// src/private/ScenarioMatrix.jsx
// scenari_futuri mode: a 2x2 uncertainty-evaluation matrix rendered below
// ScenarioTimeline (see ZoneLayer.js), inside the same
// .zone-layer__scenario-layer z-index-escape wrapper. Every labeled scenario
// branch from the timeline becomes a draggable chip the user can place
// anywhere in the quadrant box to record how uncertain/impactful it is along
// two editable axes.
import React, { useState, useEffect, useRef } from "react";
import { computeScenarioNumbers } from "../lib/scenarioTreeSummary";
// useT from langContext (NOT LanguageContext.jsx) — this module is
// reachable from scenarioCardGrid.js's import graph (via ZoneLayer.js)
// which scenarioCardGrid.test.js imports directly under jsdom; see
// ScenarioTimeline.jsx's own comment on this same constraint.
import { useT } from "../i18n/langContext";
import "./ScenarioMatrix.css";

const PADDING = 60; // margin reserved around the quadrant box for axis labels
const MIN_BOX = 160;

// Heuristic default position for a chip that hasn't been dragged yet
// (branch.matrixPos still null). The spec allows either a mentor-supplied
// matrixPos or a heuristic center-of-quadrant default — suggested_splits
// (Task 1) doesn't carry a matrixPos field, so only the heuristic default is
// implemented here (YAGNI, not a scope gap). Cycling through all four
// quadrant centers (instead of a single center point) keeps multiple
// still-unplaced chips from stacking exactly on top of one another.
const QUADRANT_CENTERS = [
  { x: 0.25, y: 0.25 },
  { x: 0.75, y: 0.25 },
  { x: 0.25, y: 0.75 },
  { x: 0.75, y: 0.75 },
];

const clamp01 = (n) => Math.min(1, Math.max(0, n));

// Editable axis-label text: local state for a responsive feel while typing,
// committed to parent state on blur/Enter. Mirrors ScenarioTimeline.jsx's
// BranchLabelInput (itself modeled on PostItNode.js's edit-then-commit
// pattern) — the actual onBlur-commit inline-text pattern already
// established for this same feature, closer to the brief's description than
// BrainstormPanel.jsx's BrainstormEntry (which commits on every keystroke via
// onInput on an uncontrolled contentEditable, not on blur).
function AxisLabelInput({ value, onCommit, className, style, ariaLabel }) {
  const [text, setText] = useState(value || "");
  useEffect(() => { setText(value || ""); }, [value]);
  const commit = () => { if (text !== value) onCommit(text); };
  return (
    <input
      type="text"
      className={className}
      style={style}
      value={text}
      aria-label={ariaLabel}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
    />
  );
}

// One draggable chip. Plain mouse events (no react-draggable — this is a
// small self-contained drag, not the full machinery Chatbot.jsx uses).
// Normalizes the pointer position to 0-1 within the quadrant box's own
// bounding rect, which reflects on-screen size/position regardless of the
// canvas's current pan/zoom transform, so no zoom-factor math is needed.
function MatrixChip({ branch, number, pos, boxRef, onUpdatePos }) {
  const [dragPos, setDragPos] = useState(null);
  const draggingRef = useRef(false);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = boxRef.current.getBoundingClientRect();
    draggingRef.current = true;

    const toPos = (ev) => ({
      x: clamp01((ev.clientX - rect.left) / rect.width),
      y: clamp01((ev.clientY - rect.top) / rect.height),
    });

    const handleMove = (ev) => {
      if (!draggingRef.current) return;
      setDragPos(toPos(ev));
    };
    const handleUp = (ev) => {
      draggingRef.current = false;
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      setDragPos(null);
      onUpdatePos(branch.id, toPos(ev));
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  const effective = dragPos || pos;
  return (
    <div
      className={`scenario-matrix__chip${dragPos ? " is-dragging" : ""}`}
      style={{ left: `${effective.x * 100}%`, top: `${effective.y * 100}%` }}
      onMouseDown={handleMouseDown}
      title={branch.label}
    >
      {number != null && <span className="scenario-matrix__chip-number">{number}</span>}
      {branch.label}
    </div>
  );
}

export default function ScenarioMatrix({
  bounds,
  splits = [],
  branches = [],
  axisLabels,
  onAxisLabelChange,
  onUpdatePos,
}) {
  const t = useT();
  // Falls back to the translated default axis names when the caller (or a
  // session saved before this field existed) doesn't supply axisLabels —
  // matches InfiniteCanvas.js's own matrixAxisLabels state default.
  const resolvedAxisLabels = axisLabels || { a: t('canvas.matrix.axisA'), b: t('canvas.matrix.axisB') };
  const boxRef = useRef(null);
  const boxSize = Math.max(MIN_BOX, Math.min(bounds.w, bounds.h) - PADDING * 2);
  const boxLeft = (bounds.w - boxSize) / 2;
  const boxTop = (bounds.h - boxSize) / 2;

  // Placeholder split rows with no branch label yet shouldn't clutter the
  // matrix with an empty chip.
  const labeled = branches.filter((b) => b.label && b.label.trim());
  // Every chip — not just level-1 ones — shows its scenario's number: a
  // deep chip still needs to read as "part of scenario 2" for the matrix
  // comparison to make sense. Shared with ScenarioTimeline.jsx (which only
  // renders the number on the level-1 pill) so the two views can't disagree.
  const scenarioNumbers = computeScenarioNumbers(splits, branches);

  return (
    <div
      className="scenario-matrix"
      style={{ left: bounds.x, top: bounds.y, width: bounds.w, height: bounds.h }}
    >
      <div
        className="scenario-matrix__box"
        ref={boxRef}
        style={{ left: boxLeft, top: boxTop, width: boxSize, height: boxSize }}
      >
        <span className="scenario-matrix__crosshair scenario-matrix__crosshair--v" />
        <span className="scenario-matrix__crosshair scenario-matrix__crosshair--h" />
        {labeled.map((branch, i) => (
          <MatrixChip
            key={branch.id}
            branch={branch}
            number={scenarioNumbers[branch.id]}
            pos={branch.matrixPos || QUADRANT_CENTERS[i % QUADRANT_CENTERS.length]}
            boxRef={boxRef}
            onUpdatePos={onUpdatePos}
          />
        ))}
      </div>

      <AxisLabelInput
        className="scenario-matrix__axis-label scenario-matrix__axis-label--x"
        style={{ left: boxLeft + boxSize / 2, top: boxTop + boxSize + 18 }}
        value={resolvedAxisLabels.b}
        ariaLabel={t('canvas.matrix.axisXAria')}
        onCommit={(text) => onAxisLabelChange && onAxisLabelChange("b", text)}
      />
      <AxisLabelInput
        className="scenario-matrix__axis-label scenario-matrix__axis-label--y"
        style={{ left: boxLeft - 18, top: boxTop + boxSize / 2 }}
        value={resolvedAxisLabels.a}
        ariaLabel={t('canvas.matrix.axisYAria')}
        onCommit={(text) => onAxisLabelChange && onAxisLabelChange("a", text)}
      />
    </div>
  );
}
